import { TitanPanel } from "@lib/titan2/dist/game/control/TitanPanel";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { CommonLang } from "@lib/titan2/dist/game/view/lang/CommonLang";
import { Dictionary } from "@lib/titan2/dist/util/collection/Dictionary";
import { KeyValuePair } from "@lib/titan2/dist/util/collection/KeyValuePair";
import { EventHandler } from "@lib/titan2/dist/util/event/EventHandler";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { SlingoEvent } from "../../../controller/event/SlingoEvent";
import { SlingoLang } from "../lang/SlingoLang";
import { AbstractGameView } from "./AbstractGameView";
/**
 * Summary.ts
 */
export class Summary extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.getCurrentProperties();
    }
    createModel() {
        super.createModel();
        this.result = this.model.getResultData();
    }
    create() {
        super.create();
        this.createBackground();
        this.createTitleField();
        this.createCellPanel();
        this.createCells();
        this.createWinAmountBackground();
        this.createWinAmountField();
    }
    postCreate() {
        super.postCreate();
        this.reset();
        Logger.addObjectRef(this, "GameSummary");
    }
    createBackground() {
        this.backgrounds = new Dictionary();
        this.backgrounds.setValue("default", new TitanSprite(this.landscapeProperties.backgroundProperties, this.portraitProperties.backgroundProperties));
        this.backgrounds.setValue("fullHouse", new TitanSprite(this.landscapeProperties.fullHouseBackgroundProperties, this.portraitProperties.fullHouseBackgroundProperties));
        this.backgrounds.setValue("bigWin", new TitanSprite(this.landscapeProperties.bigWinBackgroundProperties, this.portraitProperties.bigWinBackgroundProperties));
        this.background = this.backgrounds.getValue("default");
        this.addChild(this.background);
    }
    createTitleField() {
        this.titleField = ViewUtils.createField(this, this.landscapeProperties.titleFieldProperties, this.portraitProperties.titleFieldProperties);
    }
    createCellPanel() {
        this.activeCells = [];
        this.cellPanel = new TitanPanel(this.landscapeProperties.cellPanelProperties, this.portraitProperties.cellPanelProperties);
    }
    createCells() {
        this.slingoCell = this.createCell(this.uiProperties.slingoCellProperties);
        this.jokerCell = this.createCell(this.uiProperties.jokerCellProperties);
        this.symbolWinCell = this.createCell(this.uiProperties.symbolWinCellProperties);
    }
    createCell(properties) {
        let cell = null;
        if (properties) {
            cell = this.factory.createSummaryCell(properties);
            cell.onComplete(this.handleCellComplete, this);
        }
        return cell;
    }
    createWinAmountBackground() {
        this.winAmountBackground = new TitanSprite(this.landscapeProperties.winAmountBackgroundProperties, this.portraitProperties.winAmountBackgroundProperties);
        this.addChild(this.winAmountBackground);
    }
    createWinAmountField() {
        this.winAmountField = ViewUtils.createField(this, this.landscapeProperties.winAmountFieldProperties, this.portraitProperties.winAmountFieldProperties);
    }
    play() {
        this.updateTitle();
        this.updateCells();
    }
    update() {
        this.result = this.model.getResultData();
        if (this.result.isFullHouse() && !this.result.isLowWin()) {
            this.updateBackground("fullHouse");
        }
        else if (this.result.isBigWin() && !this.result.isLowWin()) {
            this.updateBackground("bigWin");
        }
        else if (this.result.isLowWin()) {
            this.updateBackground("default");
        }
        else if (this.result.isSmallWin()) {
            this.updateBackground("default");
        }
        else {
            this.updateBackground("default");
        }
    }
    updateBackground(key) {
        const bgIndex = this.getChildIndex(this.background);
        this.removeChild(this.background);
        this.background = this.backgrounds.getValue(key);
        this.addChildAt(this.background, bgIndex);
    }
    updateTitle() {
        let key;
        if (this.result.isFullHouse() && !this.result.isLowWin()) {
            key = SlingoLang.FULL_HOUSE;
        }
        else if (this.result.isBigWin() && !this.result.isLowWin()) {
            key = SlingoLang.BIG_WIN;
        }
        else if (this.result.isLowWin() || this.result.isSmallWin()) {
            key = SlingoLang.RESULTS;
        }
        else {
            key = SlingoLang.BIG_WIN_1;
        }
        this.titleField.setText(this.langManager.getLang(key));
        this.titleField.visible = true;
    }
    updateCells() {
        this.cellPanel.removeAll();
        this.activeCells = [];
        this.cellsComplete = 0;
        if (this.result.hasNoWin()) {
            this.updateFields();
        }
        else {
            if (this.result.hasSlingoWin()) {
                this.updateSlingoWinCell(this.result);
            }
            if (this.result.hasJokerWin()) {
                this.updateJokerWinCell(this.result);
            }
            if (this.result.hasSymbolWin()) {
                this.updateSymbolWinCell(this.result);
            }
            this.cellPanel.arrange();
            this.addChild(this.cellPanel);
            let delay = 0;
            for (let i = 0; i < this.activeCells.length; i++) {
                this.callAfter(delay, this.handlePlayCell, i);
                delay += 1000;
            }
        }
    }
    updateSlingoWinCell(result) {
        if (this.slingoCell) {
            const slingoWin = result.getSlingoWin();
            const matchedPatterns = this.model.getMatchedPatterns().size();
            const slingoLabel = this.langManager.getLang(SlingoLang.SLINGO_CELL).replace("{param.matchedPatterns}", String(matchedPatterns));
            this.slingoCell.update(slingoWin, slingoLabel);
            this.activeCells.push(this.slingoCell);
            this.cellPanel.addToPanel(this.slingoCell, false);
        }
    }
    updateJokerWinCell(result) {
        if (this.jokerCell) {
            let jokerWin = result.getJokerWin();
            this.jokerCell.update(jokerWin.getAmount());
            this.activeCells.push(this.jokerCell);
            this.cellPanel.addToPanel(this.jokerCell, false);
        }
    }
    updateSymbolWinCell(result) {
        if (this.symbolWinCell) {
            let symbolWin = result.getSymbolWin();
            this.symbolWinCell.update(symbolWin);
            this.activeCells.push(this.symbolWinCell);
            this.cellPanel.addToPanel(this.symbolWinCell, false);
        }
    }
    handlePlayCell(index) {
        this.activeCells[index].play();
    }
    handleCellComplete() {
        this.cellsComplete++;
        if (this.cellsComplete == this.activeCells.length) {
            this.updateFields();
        }
    }
    updateFields() {
        let delay = this.uiProperties.updateFieldInterval;
        this.callAfter(delay, this.updateWinField);
        delay += this.uiProperties.dialogCompleteInterval;
        this.callAfter(delay, this.handleDialogComplete);
    }
    updateWinField() {
        let totalWin = this.formatCurrency(this.model.getTotalWin());
        let params = [new KeyValuePair(CommonLang.PARAM_AMOUNT, totalWin)];
        this.winAmountBackground.visible = true;
        this.winAmountField.setText(this.langManager.getLang(SlingoLang.WIN, params));
        this.dispatchGame(SlingoEvent.GAME_SUMMARY_COMPLETE);
    }
    reset() {
        this.titleField.visible = false;
        this.winAmountField.clear();
        for (let i = 0; i < this.activeCells.length; i++) {
            this.activeCells[i].reset();
        }
    }
    handleDialogComplete() {
        //comment these out to leave the complete summary on the screen for layout
        this.reset();
        this.callHandler(this.completeHandler);
    }
    onComplete(handler, scope) {
        this.completeHandler = new EventHandler(handler, scope);
    }
}
//# sourceMappingURL=Summary.js.map